import React from 'react';
import { Button, Flex, Select, Text, TextField, TagBadge, type SelectOption } from '@chatsdet/ui';
import { MacroRichTextField } from './MacroRichTextField';
import { ConfigSection } from './ConfigSection';
import { AttachmentPickerDialog } from '../../../features/attachments/components/AttachmentPickerDialog';
import { useResolvedAttachments } from '../../../features/attachments/api';
import { getFileTypeBadge } from '../../../features/attachments/utils';
import { useAuth } from '../../../contexts/AuthContext';

type NotificationNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

export const NotificationNodeConfig: React.FC<NotificationNodeConfigProps> = ({
  localData,
  setLocalData,
}) => {
  const { user } = useAuth();
  const channel = (localData.channel as string) ?? 'email';
  const channelOptions: SelectOption[] = [
    { id: 'email', label: 'Email' },
    ...(user?.features?.smsAgentMessaging || channel === 'sms'
      ? [{ id: 'sms', label: 'SMS' }]
      : []),
  ];
  const to = (localData.to as string) ?? '';
  const subject = (localData.subject as string) ?? '';
  const body = (localData.body as string) ?? '';
  const attachmentIds = Array.isArray(localData.attachmentIds)
    ? (localData.attachmentIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];
  const attachmentFilenames: Record<string, string> =
    localData.attachmentFilenames && typeof localData.attachmentFilenames === 'object'
      ? (localData.attachmentFilenames as Record<string, string>)
      : {};
  const resolvedAttachments = useResolvedAttachments(attachmentIds);
  const attachments = resolvedAttachments.data?.data ?? [];

  const setFilenameOverride = (id: string, value: string) =>
    setLocalData((prev) => {
      const prevMap =
        prev.attachmentFilenames && typeof prev.attachmentFilenames === 'object'
          ? { ...(prev.attachmentFilenames as Record<string, string>) }
          : {};
      if (value.trim().length === 0) {
        delete prevMap[id];
      } else {
        prevMap[id] = value;
      }
      return { ...prev, attachmentFilenames: prevMap };
    });

  return (
    <div className="space-y-4">
      <Text size="sm" className="text-muted-foreground">
        Send an outbound notification. SMS uses the phone number assigned to the upstream login
        credential.
      </Text>

      <Select
        label="Channel"
        items={channelOptions}
        selectedKey={channel}
        onSelectionChange={(key) => setLocalData((prev) => ({ ...prev, channel: String(key) }))}
      />

      <TextField
        label="To"
        placeholder={channel === 'sms' ? '+14155550123' : 'recipient@example.com'}
        value={to}
        onChange={(v) => setLocalData((prev) => ({ ...prev, to: v }))}
      />

      {channel === 'email' && (
        <MacroRichTextField
          label="Subject"
          placeholder="Email subject… (type $ for macros)"
          value={subject}
          onChange={(v) => setLocalData((prev) => ({ ...prev, subject: v }))}
        />
      )}

      <MacroRichTextField
        multiline
        label="Body"
        placeholder="Message body… (type $ for macros)"
        value={body}
        onChange={(v) => setLocalData((prev) => ({ ...prev, body: v }))}
      />

      {channel === 'email' && (
        <ConfigSection title="Attachments" count={attachmentIds.length}>
          <div>
            {attachmentIds.length > 0 && (
              <Flex align="center" justify="end" className="mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => setLocalData((prev) => ({ ...prev, attachmentIds: [] }))}
                  className="text-muted-foreground"
                >
                  Clear all
                </Button>
              </Flex>
            )}
            <Text size="sm" className="text-muted-foreground">
              Attach files to include with this notification.
            </Text>

            {attachmentIds.length > 0 && attachments.length > 0 && (
              <div className="mt-3 space-y-1">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="rounded-md bg-muted/30 px-2 py-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <TagBadge
                        name={getFileTypeBadge(attachment.contentType)}
                        color="gray"
                        hideDot
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <Text size="sm" className="truncate">
                          {attachment.filename}
                        </Text>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() =>
                          setLocalData((prev) => {
                            const nextIds = Array.isArray(prev.attachmentIds)
                              ? (prev.attachmentIds as string[]).filter(
                                  (aid) => aid !== attachment.id
                                )
                              : [];
                            const prevMap =
                              prev.attachmentFilenames &&
                              typeof prev.attachmentFilenames === 'object'
                                ? { ...(prev.attachmentFilenames as Record<string, string>) }
                                : {};
                            delete prevMap[attachment.id];
                            return {
                              ...prev,
                              attachmentIds: nextIds,
                              attachmentFilenames: prevMap,
                            };
                          })
                        }
                        className="text-muted-foreground hover:text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                    <MacroRichTextField
                      label="Send as filename (optional)"
                      placeholder={`${attachment.filename} (type $ for macros, e.g. report_$RANDOM_STRING(8).pdf)`}
                      value={attachmentFilenames[attachment.id] ?? ''}
                      onChange={(v) => setFilenameOverride(attachment.id, v)}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3">
              <AttachmentPickerDialog
                selectedIds={attachmentIds}
                onChange={(nextIds) =>
                  setLocalData((prev) => ({ ...prev, attachmentIds: nextIds }))
                }
                triggerVariant="outline"
                triggerSize="sm"
                triggerLabel="Add attachment"
              />
            </div>
          </div>
        </ConfigSection>
      )}
    </div>
  );
};
